import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, CardBody } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import Breadcrumbs            from "../../components/Common/Breadcrumb";
import "../../components/Dashboard.css";
import PageGrid               from "../../components/GridLayout/PageGrid";
import FieldCustomizerPanel, { useFieldCustomizer, StatCards, DataTable } from "../../components/FieldCustomizer/FieldCustomizer";
import "../../components/FieldCustomizer/FieldCustomizer.css";

import OverView3              from "./Overview3";
import SocialSource           from "./SocialSource";
import MachineStatusChart     from "./MachineStatusChart";
import MachineDowntimeTimeline from "./MachineDownTimeTimeLine";
import AlarmList              from "./LatestAlarms";
import DowntimeAnalyticsBar   from "./DowntimeAnalyticsBar";

/* ── Available API fields for KPI cards ──────────────────────────── */
const KPI_AVAILABLE_FIELDS = [
  { value: "total_running_hours",  label: "Running Hours"  },
  { value: "good_parts",           label: "Good Parts"     },
  { value: "bad_parts",            label: "Bad Parts"      },
  { value: "total_parts",          label: "Total Parts"    },
  { value: "stopped_time",         label: "Stopped Hours"  },
  { value: "machine_status",       label: "Machine Status" },
  { value: "part_number",          label: "Part Number"    },
  { value: "last_updated",         label: "Last Updated"   },
];

const KPI_DEFAULT_CARDS = [
  { id:"running",  label:"Running Hours",  field:"total_running_hours", icon:"ri-time-line",           color:"text-primary", visible:true },
  { id:"good",     label:"Good Parts",     field:"good_parts",          icon:"ri-checkbox-circle-line", color:"text-success", visible:true },
  { id:"bad",      label:"Bad Parts",      field:"bad_parts",           icon:"ri-close-circle-line",    color:"text-danger",  visible:true },
  { id:"total",    label:"Total Parts",    field:"total_parts",         icon:"ri-group-line",           color:"text-primary", visible:true },
  { id:"stopped",  label:"Stopped Hours",  field:"stopped_time",        icon:"ri-pause-circle-line",    color:"text-warning", visible:true },
];

/* ── Available API fields for Alarm table ────────────────────────── */
const ALARM_AVAILABLE_FIELDS = [
  { value:"date",        label:"Date"        },
  { value:"time",        label:"Time"        },
  { value:"station",     label:"Station"     },
  { value:"description", label:"Description" },
];

const ALARM_DEFAULT_COLS = [
  { id:"station",     label:"Station",     field:"station",     visible:true },
  { id:"description", label:"Description", field:"description", visible:true },
  { id:"date",        label:"Date",        field:"date",        visible:true },
  { id:"time",        label:"Time",        field:"time",        visible:true },
];

/* ── helpers ─────────────────────────────────────────────────────── */
const getProductionDate = () => {
  const now = new Date();
  const d = new Date(now);
  if (now.getHours() < 6) d.setDate(d.getDate());
  return d.toISOString().split("T")[0];
};
const getCurrentShift = () => {
  const h = new Date().getHours();
  if (h >= 6  && h < 14) return 1;
  if (h >= 14 && h < 22) return 2;
  return 3;
};
const isShiftDisabled = (shift, date) => {
  if (date !== new Date().toISOString().split("T")[0]) return false;
  const h = new Date().getHours();
  if (shift === "1") return h < 6;
  if (shift === "2") return h < 14;
  if (shift === "3") return h < 22 && h >= 6;
  return false;
};

/* ════════════════════════════════════════════════════════════════════
   DASHBOARD
════════════════════════════════════════════════════════════════════ */
const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCompanyId = localStorage.getItem("companyid");

  const [machines,       setMachines]       = useState([]);
  const [partsData,      setPartsData]      = useState({
    good_parts:0, bad_parts:0, total_parts:0,
    total_running_hours:0, stopped_time:0,
    machine_stop_time:null, machine_status:"Stopped",
    last_updated:"00:00:00", machine:"", part:"",
  });
  const [selectedDate,   setSelectedDate]   = useState(getProductionDate());
  const [selectedShift,  setSelectedShift]  = useState(getCurrentShift());
  const selectedMachineId = localStorage.getItem("selectedMachineId");
  const [selectedMachine, setSelectedMachine] = useState(selectedMachineId || location.state?.id || "");

  // Field customizer state for KPI cards
  const kpiFC = useFieldCustomizer("dash_kpi", KPI_DEFAULT_CARDS, []);
  // Field customizer state for Alarm table
  const alarmFC = useFieldCustomizer("dash_alarms", [], ALARM_DEFAULT_COLS);
  // Alarm rows state (populated by child, passed up for DataTable)
  const [alarmRows, setAlarmRows] = useState([]);

  document.title = "Dashboard | OptiMize";

  /* ── fetch machines ───────────────────────────────────────────── */
  useEffect(() => {
    fetch(`https://api.hanoch.in/api/machines/by-company/?id=${selectedCompanyId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setMachines(d?.data || []))
      .catch(console.error);
  }, []);

  /* ── fetch PLC data ───────────────────────────────────────────── */
  const fetchPLCData = async (date, shift, machine) => {
    try {
      const res = await axios.get(
        `https://api.hanoch.in/api/mcc-shift-data-view/?shift=${shift}&date=${date}&machine_id=${machine}`
      );
      const data = res;
      setPartsData({
        bad_parts:           data?.bad_products          || 0,
        good_parts:          data?.good_products         || 0,
        total_parts:         data?.total_count           || 0,
        machine_stop_time:   data?.machine_stop_time,
        total_running_hours: data?.machine_running_hours || 0,
        stopped_time:        data?.stopped_time          || 0,
        machine:             data?.machine_name          || "",
        part:                data?.part                  || "",
        part_number:         data?.part_number           || 0,
        machine_status:      data?.machine_status        || "Stopped",
        last_updated:        data?.time                  || "00:00:00",
      });
    } catch(e) {
      if (e.response?.status === 404) navigate("/404");
    }
  };

  useEffect(() => {
    if (!selectedDate || !selectedShift) return;
    fetchPLCData(selectedDate, selectedShift, selectedMachine);
    const id = setInterval(() => {
      if (selectedDate === getProductionDate() && Number(selectedShift) === getCurrentShift())
        fetchPLCData(selectedDate, selectedShift, selectedMachine);
    }, 60000);
    return () => clearInterval(id);
  }, [selectedDate, selectedShift, selectedMachine]);

  /* ── derived ──────────────────────────────────────────────────── */
  const machine_status = partsData?.machine_status === true ? "Running" : "Stopped";

  /* ── widget definitions ───────────────────────────────────────── */
  const widgets = [
    {
      id: "controls",
      label: "Controls",
      icon: "mdi mdi-cog-outline",
      defaultW: 12, defaultH: 3, minH: 2,
      content: (
        <Card className="h-100 mb-0">
          <CardBody className="p-2">
            <div className="d-flex flex-wrap align-items-center gap-3 px-2 py-1">
              <div className="d-flex align-items-center gap-1">
                <label className="text-muted mb-0" style={{fontSize:13}}>Machine:</label>
                <select className="form-control form-control-sm" style={{minWidth:140}}
                  value={selectedMachine}
                  onChange={e=>{ setSelectedMachine(e.target.value); localStorage.setItem("selectedMachineId",e.target.value); }}>
                  <option value="">Select Machine</option>
                  {machines?.map((m,i)=><option key={i} value={m?.id}>{m?.name}</option>)}
                </select>
              </div>
              <div style={{padding:"4px 10px",border:"1px solid #dee2e6",borderRadius:6}}>
                <span style={{fontWeight:600,fontSize:14}}>{partsData.part}</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="text-muted mb-0" style={{fontSize:13}}>Date:</label>
                <input type="date" value={selectedDate}
                  min="2026-01-27" max={new Date().toISOString().split("T")[0]}
                  onChange={e=>setSelectedDate(e.target.value)}
                  style={{padding:"4px 8px",border:"1px solid #dee2e6",borderRadius:6,fontSize:13}} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="text-muted mb-0" style={{fontSize:13}}>Shift:</label>
                <select value={selectedShift} onChange={e=>setSelectedShift(e.target.value)}
                  style={{padding:"4px 8px",border:"1px solid #dee2e6",borderRadius:6,fontSize:13}}>
                  <option value="">Select</option>
                  <option value="1" disabled={isShiftDisabled("1",selectedDate)}>6-2 (Shift 1)</option>
                  <option value="2" disabled={isShiftDisabled("2",selectedDate)}>2-10 (Shift 2)</option>
                  <option value="3" disabled={isShiftDisabled("3",selectedDate)}>10-6 (Shift 3)</option>
                </select>
              </div>
              <div className="d-flex align-items-center ms-auto gap-2">
                <span className="text-muted" style={{fontSize:12}}>Updated: {partsData?.last_updated}</span>
                <div className="blinking-text">
                  <img width="18" height="18"
                    src={machine_status==="Running"?"https://img.icons8.com/ios-filled/50/40C057/filled-circle.png":"https://img.icons8.com/ios-filled/50/FA5252/filled-circle.png"}
                    alt={machine_status}/>
                </div>
                <span style={{fontSize:13,fontWeight:500}}>{machine_status}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      ),
    },
    {
      id: "kpi",
      label: "KPI Stats",
      icon: "mdi mdi-view-dashboard-outline",
      defaultW: 12, defaultH: 4, minH: 3,
      // Field customizer for KPI cards
      customizer: (
        <FieldCustomizerPanel
          cards={kpiFC.cards}
          columns={[]}
          availableFields={KPI_AVAILABLE_FIELDS}
          onCardsChange={kpiFC.updateCards}
          onColumnsChange={()=>{}}
          onReset={kpiFC.resetFields}
        />
      ),
      content: <StatCards cards={kpiFC.cards} data={partsData} />,
    },
    {
      id: "overview",
      label: "Production Chart",
      icon: "mdi mdi-chart-line",
      defaultW: 7, defaultH: 7, minH: 5,
      defaultX: 0, defaultY: 7,
      content: <OverView3 partsDataMap={{}} selectedDate={selectedDate} selectedShift={selectedShift} selectedMachine={selectedMachine} selectedChildPart={4} />,
    },
    {
      id: "social",
      label: "Parts Breakdown",
      icon: "mdi mdi-chart-donut",
      defaultW: 5, defaultH: 7, minH: 5,
      defaultX: 7, defaultY: 7,
      content: <SocialSource totalpartsData={{}} selectedDate={selectedDate} selectedShift={selectedShift} selectedMachine={selectedMachine} />,
    },
    {
      id: "machinestatus",
      label: "Machine Status",
      icon: "mdi mdi-chip",
      defaultW: 12, defaultH: 6, minH: 4,
      content: <MachineStatusChart selectedDate={selectedDate} selectedShift={selectedShift} selectedMachine={selectedMachine} />,
    },
    {
      id: "downtime",
      label: "Downtime Timeline",
      icon: "mdi mdi-timeline-clock-outline",
      defaultW: 12, defaultH: 6, minH: 4,
      content: <MachineDowntimeTimeline machineId={selectedMachine} date={selectedDate} shift={selectedShift} />,
    },
    {
      id: "alarms",
      label: "Latest Alarms",
      icon: "mdi mdi-bell-alert-outline",
      defaultW: 7, defaultH: 7, minH: 4,
      // Field customizer for Alarm table columns
      customizer: (
        <FieldCustomizerPanel
          cards={[]}
          columns={alarmFC.columns}
          availableFields={ALARM_AVAILABLE_FIELDS}
          onCardsChange={()=>{}}
          onColumnsChange={alarmFC.updateCols}
          onReset={alarmFC.resetFields}
        />
      ),
      content: (
        <AlarmList
          selectedDate={selectedDate}
          selectedShift={selectedShift}
          selectedMachine={selectedMachine}
          columnConfig={alarmFC.columns}
        />
      ),
    },
    {
      id: "analytics",
      label: "Downtime Analytics",
      icon: "mdi mdi-chart-bar",
      defaultW: 5, defaultH: 7, minH: 4,
      defaultX: 7,
      content: <DowntimeAnalyticsBar machineId={selectedMachine} />,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Dashboard" />
          <PageGrid pageKey="dashboard" widgets={widgets} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
